import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Resolver } from 'react-hook-form'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { Alert } from '@/components/ui/Alert'
import { Modal } from '@/components/ui/Modal'
import { Button, Select, Input, FileInput } from '@/components/ui'
import { useUploadBankStatement } from '@/hooks/useBankReconciliation'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useNotificationStore } from '@/stores'
import type { BankStatementUploadWarning } from '@/types'

interface UploadModalFormData {
  bank_name: 'scb' | 'kbank' | 'bbl'
  password: string
  file: FileList
}

const uploadModalSchema = z.object({
  bank_name: z.enum(['scb', 'kbank', 'bbl']),
  password: z.string().optional().default(''),
  file: z.any().refine((v: FileList) => v && v.length > 0, 'File is required'),
}) satisfies z.ZodType<UploadModalFormData>

export interface UploadStatementModalProps {
  isOpen: boolean
  onClose: () => void
}

export function UploadStatementModal({ isOpen, onClose }: UploadStatementModalProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const uploadStatement = useUploadBankStatement()
  const addToast = useNotificationStore((s) => s.addToast)
  const formatDate = useDateFormatter()
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  // T131-006: an overlap is a WARNING, not an error — the upload already
  // succeeded. Kept separate from formError so it renders in its own Alert
  // and keeps the modal open (instead of auto-closing) so the operator can
  // actually read which period overlapped before dismissing it.
  const [overlapWarning, setOverlapWarning] = useState<BankStatementUploadWarning | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UploadModalFormData>({
    resolver: zodResolver(uploadModalSchema) as unknown as Resolver<UploadModalFormData>,
    defaultValues: {
      bank_name: 'kbank',
      password: '',
    },
  })

  const handleClose = () => {
    reset()
    setFormError(null)
    setOverlapWarning(null)
    setShowPassword(false)
    onClose()
  }

  const onSubmit = async (data: UploadModalFormData) => {
    setFormError(null)
    try {
      const result = await uploadStatement.mutateAsync({
        file: data.file[0],
        bank_name: data.bank_name,
        password: data.password || undefined,
      })
      // Always report the outcome — a silent success is exactly what this
      // epic exists to fix. skipped > 0 is the normal, healthy result of an
      // overlapping re-upload (rows already present are deduped), not a
      // failure, so it's worded as such rather than surfaced as an error.
      const { imported, skipped } = result.import_summary
      addToast({
        type: 'success',
        message: skipped > 0
          ? t('messages.upload_success_imported_with_skipped', { count: imported, skipped })
          : t('messages.upload_success_imported', { count: imported }),
      })
      if (result.warning) {
        setOverlapWarning(result.warning)
        return
      }
      handleClose()
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { error?: { code?: string; message?: string } } }
      }
      const errorData = error?.response?.data?.error
      if (errorData?.code === 'wrong_password') {
        setFormError(t('errors.wrong_password'))
      } else if (errorData?.code === 'unsupported_format') {
        setFormError(t('errors.unsupported_format'))
      } else if (errorData?.code === 'empty_file') {
        setFormError(t('errors.empty_file'))
      } else {
        setFormError(errorData?.message || t('messages.error_loading'))
      }
    }
  }

  const bankOptions = [
    { value: 'kbank', label: t('banks.kbank') },
    { value: 'scb', label: t('banks.scb') },
    { value: 'bbl', label: t('banks.bbl') },
  ]

  // T131-006: once an overlap warning is showing, the upload has already
  // succeeded — there's nothing left to submit, so the footer collapses to a
  // single acknowledgement button. It's advisory only: it never disables or
  // otherwise blocks the operator from closing the modal.
  const footer = overlapWarning ? (
    <Button type="button" onClick={handleClose}>
      {tc('actions.close')}
    </Button>
  ) : (
    <>
      <Button type="button" variant="ghost" onClick={handleClose}>
        {tc('actions.cancel')}
      </Button>
      <Button
        type="submit"
        form="upload-statement-form"
        loading={uploadStatement.isPending}
      >
        {t('actions.upload')}
      </Button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('statements.new')}
      footer={footer}
      size="lg"
    >
      {overlapWarning ? (
        // Overlap fields are rendered generically (just start_date/end_date
        // per entry) rather than assuming today's exact shape, so a later
        // backend enrichment (e.g. bank-account-aware messaging) doesn't
        // require a matching FE change.
        <Alert variant="warning" title={t('labels.overlap_warning_title')}>
          <p>{t('messages.overlap_warning_intro')}</p>
          <ul className="mt-2 list-disc list-inside space-y-1">
            {overlapWarning.overlaps.map((overlap, idx) => (
              <li key={overlap.statement_id ?? idx}>
                {formatDate(overlap.start_date)} – {formatDate(overlap.end_date)}
              </li>
            ))}
          </ul>
        </Alert>
      ) : (
        <form id="upload-statement-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {formError && (
            <Alert variant="error" icon={null}>
              <span>{formError}</span>
            </Alert>
          )}

          <Select
            label={t('labels.bank_name')}
            options={bankOptions}
            error={errors.bank_name?.message ? tc('errors.required') : undefined}
            {...register('bank_name')}
          />

          <div>
            <label className="block text-sm font-medium mb-1">{t('labels.file')}</label>
            <FileInput
              accept=".csv,.pdf"
              aria-label={t('labels.file')}
              {...register('file')}
            />
            <p className="mt-1 text-xs text-base-content/60">{t('labels.file_hint')}</p>
            {errors.file && (
              <p className="mt-1 text-sm text-error">{tc('errors.required')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('labels.password')}</label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                inputClassName="pr-10"
                placeholder={t('labels.password_placeholder')}
                {...register('password')}
              />
              <Button
                type="button"
                shape="circle"
                variant="ghost"
                size="xs"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  )
}
